<template>
   <Modal
      size="sm"
      v-model="model"
      :loading="user.isLoading.value"
      :title="
         userId
            ? user.data.value?.[0].user_email || ''
            : $t('web.components.organization-user-modal.title')
      "
      :description="
         userId
            ? $t('web.components.organization-user-modal.description')
            : $t('web.components.organization-user-modal.description-create')
      "
      :confirm-button="userId != jwt?.user_details?.id ? confirmButton : undefined"
      :cancel-button="userId && userId != jwt?.user_details?.id ? cancelButton : undefined"
   >
      <Form ref="formRef" v-model="formData" @submit="submit" @dirty="(e) => (dirty = e)">
         <div class="__form-row">
            <TextInput grow v-bind="formData.email" v-model="formData.email.value" />
            <Select v-bind="formData.role" v-model="formData.role.value" />
         </div>
      </Form>
   </Modal>
</template>

<script lang="ts" setup>
import type Form from '~/components/core/Form.vue'
import { useDeleteUserOrganization } from '~/composables/api/mutations/delete-user-organization'
import { useUpsertUserOrganization } from '~/composables/api/mutations/upsert-user-organization'
import { useUserOrganization } from '~/composables/api/queries/user-organization'
import { useGetJwt } from '~/composables/api/supabase/get-jwt'

type Props = {
   userId?: string
   size?: 'xl' | 'lg' | 'md' | 'sm' | 'fit'
}

const props = withDefaults(defineProps<Props>(), {})

const { t } = useI18n()
const model = defineModel<boolean>()

const jwt = useGetJwt()
const organizationId = useOrganizationId()

const roleOptions = [
   { id: 'UOR_ADMIN', value: t('enum.UOR_ADMIN') },
   { id: 'UOR_EDITOR', value: t('enum.UOR_EDITOR') },
   { id: 'UOR_VIEWER', value: t('enum.UOR_VIEWER') },
]

const isEditMode = computed(() => !!props.userId)

const upsertUserOrganization = useUpsertUserOrganization({
   confirmation: {
      type: 'save',
   },
})

const deleteUserOrganization = useDeleteUserOrganization({
   confirmation: {
      type: 'delete',
   },
})

const user = useUserOrganization(
   computed(() => ({
      userId: props.userId || '',
      organizationId: organizationId.value,
      enabled: !!props.userId,
   }))
)

const { formData, formRef, dirty, confirmButton, cancelButton, submit } = useEntityModal({
   open: model,
   isEdit: isEditMode,
   scopes: {
      create: ['organization:users:create'],
      update: ['organization:users:update'],
      delete: ['organization:users:delete'],
   },
   fields: (formDisabled) => ({
      email: {
         value: user.data.value?.[0].user_email || '',
         label: t('form.email'),
         placeholder: t('form.email-placeholder'),
         type: 'email' as const,
         required: true,
         disabled: formDisabled.value || !!props.userId,
         validator: isEmail,
      },
      role: {
         value: user.data.value?.[0].role || 'UOR_ADMIN',
         label: t('form.poi-form.type'),
         placeholder: t('common.select'),
         type: 'select' as const,
         options: roleOptions,
         required: true,
         disabled: formDisabled.value || props.userId === jwt.value?.user_details?.id,
      },
   }),
   save: upsertUserOrganization,
   remove: deleteUserOrganization,
   toSavePayload: (data) => ({
      p_user_id: props.userId,
      p_user_email: data.email.value,
      p_organization_id: organizationId.value,
      p_role: data.role.value,
   }),
   toRemovePayload: () => ({
      p_user_id: props.userId || null,
      p_organization_id: organizationId.value,
   }),
})
</script>
